import { EXTENSION_STORE_URL } from '@/constants/browserExtension';
import { safeAccessibilityRole } from '@/constants/utils/a11y';
import React, { useMemo } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import Icon from '@/components/Icon';
import MacAppButton from '@/components/Buttons/MacAppButton';
import MetaHead from '@/components/MetaHead';
import PublicFooter from '@/components/PublicFooter';
import Text from '@/components/Typography/Text';
import FlowField from '@/components/Landing/FlowField';
import LandingClosing from '@/components/Landing/LandingClosing';
import Reveal from '@/components/Landing/Reveal';
import useLandingStyles from '@/components/Landing/useLandingStyles';
import {
  Accent,
  BleedRule,
  Chip,
  CtaRow,
  DisplayHeading,
  GhostCta,
  Kicker,
  Lede,
  Marquee,
  PrimaryCta,
  Section,
  Split,
  useEntrance,
  useInteractive,
  useLanding,
} from '@/components/Landing/LandingKit';
import {
  LAYOUT,
  fluid,
  isWeb,
  labelType,
} from '@/components/Landing/landingTokens';
import { isDarkTheme } from '@/constants/Colors';
import { useTheme } from '@/context/ThemeContext';
import {
  FREE_USER_STARTING_CREDITS_DISPLAY,
  GAB_AI_SOFTWARE_ID,
} from '@/constants/config';
import {
  DOWNLOAD_MARQUEE,
  DOWNLOAD_PLATFORMS,
  DOWNLOAD_SYNC_POINTS,
} from '@/constants/publicDownloads';
import { openStoreUrl } from '@/components/PublicFooter/publicFooterStoreLinks';

const SITE = 'https://gab.ai';

const FAQS = [
  {
    q: 'Is the Gab AI chat app free?',
    a: 'Yes. The iOS, Android, macOS, and Windows apps are free to install, and a new account comes with credits to start — no credit card required. Plus unlocks Work mode for multi-step jobs, free default-model messages, and more credits each month.',
  },
  {
    q: 'Do my chats sync between phone, Mac, Windows, and the web?',
    a: 'Yes. Sign in with the same account everywhere. Conversations, memory, files, agents, and your credit balance stay in sync automatically.',
  },
  {
    q: 'How do I get Gab AI on Windows?',
    a: 'Install Gab AI from the Microsoft Store using the Windows download button above. Open the app and sign in with your existing Gab AI account. The app supports Windows 10 version 2004 or newer and Windows 11, and receives updates through the Microsoft Store.',
  },
  {
    q: 'Can I use Gab AI on Linux or Chromebook?',
    a: 'Yes. Open gab.ai in any modern browser for the full web workspace — same models, same history, nothing to install. There is no native Linux app yet.',
  },
  {
    q: 'How do I get Gab AI on my Mac?',
    a: 'Download the Mac app directly from Gab AI using the Download for Mac button. Visit the dedicated Mac page to explore local project folders, file changes, and native Mac features.',
  },
  {
    q: 'Can I subscribe on mobile?',
    a: 'Yes. Plus and credit packs are available inside the iOS and Android apps through the store billing Apple and Google provide, as well as on the web.',
  },
  {
    q: 'Are mobile features the same as the web?',
    a: 'The same models, generators, agents, tasks, and memory are available on every platform. A few power-user surfaces — like the full deck workspace — are widest on desktop web, but chat and creation are parity everywhere.',
  },
];

/** One store badge or web CTA inside a platform card. */
function PlatformAction({ platform, isXS }) {
  const { hovered, handlers } = useInteractive();

  if (platform.key === 'mac') return <MacAppButton download />;

  if (!platform.badge) {
    return <PrimaryCta label="Start free" href={platform.url} full={isXS} />;
  }

  const Badge = platform.badge;
  const width = Math.round(platform.badgeWidth * (isXS ? 0.94 : 1));

  return (
    <Pressable
      {...handlers}
      {...(isWeb
        ? { href: platform.url, hrefAttrs: { target: '_blank', rel: 'noopener noreferrer' } }
        : { onPress: () => openStoreUrl(platform.url) })}
      accessibilityRole="link"
      accessibilityLabel={platform.badgeLabel}
      style={{
        opacity: hovered ? 0.82 : 1,
        transform: [{ scale: isWeb && hovered ? 1.03 : 1 }],
        ...(isWeb ? { cursor: 'pointer', transition: 'transform 220ms ease, opacity 220ms ease' } : {}),
      }}
    >
      <Badge width={width} />
    </Pressable>
  );
}

/** Editorial card for one download surface. */
function PlatformCard({ platform }) {
  const { c, isXS, display } = useLanding();
  const h3 = display({ min: 22, max: 30, leading: 1.12, tracking: -0.032 });
  const isWebPlatform = platform.key === 'web';

  return (
    <View
      style={{
        flexGrow: 1,
        flexBasis: isXS ? '100%' : '46%',
        minWidth: 0,
        borderRadius: 22,
        borderWidth: 1,
        borderColor: isWebPlatform ? c.accentRing : c.rule,
        backgroundColor: isWebPlatform ? c.accentSoft : c.cardSolid,
        padding: isXS ? 22 : 28,
        gap: isXS ? 20 : 26,
      }}
    >
      <View style={{ gap: 12 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <View
            style={{
              width: 34,
              height: 34,
              borderRadius: 11,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isWebPlatform ? c.accentSoft : c.panel,
            }}
          >
            <Icon name={platform.icon} size={14} color={c.accent} solid />
          </View>
          <Text weight="bold" style={{ color: c.faint, ...labelType(9.5, 0.2) }}>
            {platform.kicker}
          </Text>
        </View>

        <DisplayHeading
          tag="h3"
          mask={false}
          type={h3}
          lines={[[platform.name]]}
        />

        <Text style={{ color: c.muted, fontSize: 14, lineHeight: 22, maxWidth: 460 }}>
          {platform.description}
        </Text>

        <Text style={{ color: c.faint, fontSize: 11.5, letterSpacing: 0.15 }}>
          {platform.requirements}
        </Text>
      </View>

      <View style={{ gap: 14 }}>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 7 }}>
          {platform.highlights.map((label) => (
            <Chip key={label}>{label}</Chip>
          ))}
        </View>
        <PlatformAction platform={platform} isXS={isXS} />
        {platform.detailsHref && <GhostCta label={platform.detailsLabel} href={platform.detailsHref} />}
      </View>
    </View>
  );
}

/** Sync band row — icon, title, one line of proof. */
function SyncRow({ point, isLast, width }) {
  const { c } = useLanding();

  return (
    <View
      style={{
        flexDirection: 'row',
        gap: 14,
        paddingVertical: fluid(width, 16, 20),
        borderBottomWidth: isLast ? 0 : 1,
        borderBottomColor: c.rule,
      }}
    >
      <View
        style={{
          width: 34,
          height: 34,
          borderRadius: 11,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: c.accentSoft,
        }}
      >
        <Icon name={point.icon} size={13} color={c.accent} solid />
      </View>
      <View style={{ flex: 1, minWidth: 0, gap: 4 }}>
        <Text weight="bold" style={{ color: c.ink, fontSize: 15, letterSpacing: -0.15 }}>
          {point.title}
        </Text>
        <Text style={{ color: c.muted, fontSize: 13.5, lineHeight: 21 }}>
          {point.detail}
        </Text>
      </View>
    </View>
  );
}

export default function DownloadPage() {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const insets = useSafeAreaInsets();
  const { c, band, isXS, isCompact, width, pad, display } = useLanding();
  const entered = useEntrance();

  useLandingStyles();

  const headline = display({ min: 38, max: 78, leading: 0.96, tracking: -0.042 });
  const h2 = display({ min: 30, max: 52, leading: 1.02, tracking: -0.038 });

  const structuredData = useMemo(() => ({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${SITE}/download#page`,
        url: `${SITE}/download`,
        name: 'Gab AI chat app',
        description: 'The Gab AI chat app for iPhone, iPad, Android, Mac, and Windows, plus the full workspace in any browser. One account syncs everywhere.',
        isPartOf: { '@id': GAB_AI_SOFTWARE_ID },
        breadcrumb: { '@id': `${SITE}/download#breadcrumb` },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${SITE}/download#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Gab AI', item: SITE },
          { '@type': 'ListItem', position: 2, name: 'Download', item: `${SITE}/download` },
        ],
      },
      {
        // Same node as the site-wide one in `app/+html.jsx`, referenced by @id so
        // `downloadUrl` merges into the product entity instead of standing up a
        // second SoftwareApplication without its aggregateRating.
        '@type': 'SoftwareApplication',
        '@id': GAB_AI_SOFTWARE_ID,
        name: 'Gab AI',
        alternateName: 'Gab AI chat app',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web, iOS, Android, macOS, Windows',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
        downloadUrl: DOWNLOAD_PLATFORMS
          .filter((p) => p.external)
          .map((p) => p.url),
      },
      {
        '@type': 'ItemList',
        '@id': `${SITE}/download#platforms`,
        name: 'Gab AI download options',
        itemListElement: DOWNLOAD_PLATFORMS.map((platform, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: platform.name,
          description: platform.description,
          url: platform.external ? platform.url : `${SITE}${platform.url}`,
        })),
      },
      {
        '@type': 'FAQPage',
        '@id': `${SITE}/download#faq`,
        mainEntity: FAQS.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  }), []);

  return (
    <View style={{ flex: 1, backgroundColor: colors.solid_color_primary }}>
      <FlowField isDark={isDark} />
      <MetaHead
        title="Download Gab AI — Windows, Mac, iPhone & Android"
        description="A free AI chat app for Windows, Mac, iPhone, iPad, and Android — or open it in any browser. One account syncs chat, memory, files, and credits across devices."
        canonical="/download"
        keywords="AI chat app, AI chatbot app, free AI chat app, download Gab AI, Windows AI app, Microsoft Store, iOS AI app, Android AI app, Mac AI app"
        structuredData={structuredData}
      />

      <ScrollView
        {...(isWeb ? { dataSet: { landingScroll: 'true' } } : {})}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ alignItems: 'center' }}
        accessibilityRole={safeAccessibilityRole('main')}
        nativeID="main-content"
        style={{ position: 'relative', zIndex: 1 }}
      >
        {/* ============ MASTHEAD ============ */}
        <View
          accessibilityRole={isWeb ? 'region' : undefined}
          accessibilityLabel="Download the Gab AI chat app"
          style={{
            width: '100%',
            maxWidth: LAYOUT.max,
            paddingHorizontal: pad,
            paddingTop: (isXS ? insets.top : 0) + fluid(width, 104, 152),
            paddingBottom: fluid(width, 40, 60),
          }}
        >
          <View
            style={isWeb ? {
              opacity: entered ? 1 : 0,
              transition: 'opacity 700ms ease 120ms',
            } : null}
          >
            <Kicker>Apps everywhere</Kicker>
          </View>

          <DisplayHeading
            tag="h1"
            entered={entered}
            type={headline}
            style={{ marginTop: fluid(width, 22, 32) }}
            lines={[
              ['The AI chat app'],
              [<Accent key="a" size={headline.fontSize} leading={headline.lineHeight}>for every device</Accent>, '.'],
            ]}
          />

          <View style={{ height: 1, backgroundColor: c.rule, marginTop: fluid(width, 30, 48) }} />

          <View
            style={{
              flexDirection: isCompact ? 'column' : 'row',
              gap: isCompact ? 30 : 56,
              alignItems: isCompact ? 'stretch' : 'flex-start',
              paddingTop: fluid(width, 26, 40),
              ...(isWeb ? {
                opacity: entered ? 1 : 0,
                transform: [{ translateY: entered ? 0 : 18 }],
                transition: 'opacity 800ms ease 420ms, transform 800ms ease 420ms',
              } : {}),
            }}
          >
            <View style={{ flex: isCompact ? undefined : 1.15, minWidth: 0 }}>
              <Lede max={470}>
                A free AI chat app for iPhone, iPad, Android, Mac, and Windows — plus the
                same workspace in any browser. Install once, sign in once, and
                your chats, memory, files, and credits follow you automatically.
              </Lede>
            </View>

            <View style={{ flex: isCompact ? undefined : 1, minWidth: 0, gap: 18 }}>
              <CtaRow>
                <PrimaryCta label="Start free" full={isXS} />
                <GhostCta label="See all features" href="/features" />
              </CtaRow>
              <Text style={{ color: c.faint, fontSize: 12.5, letterSpacing: 0.1 }}>
                {FREE_USER_STARTING_CREDITS_DISPLAY} credits to start · No credit card required
              </Text>
            </View>
          </View>
        </View>

        <BleedRule />
        <Marquee items={DOWNLOAD_MARQUEE} />
        <BleedRule />

        {/* ============ PLATFORM CARDS ============ */}
        <Section label="Choose your platform" top={false}>
          <Reveal>
            <View
              style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: isXS ? 12 : 16,
                marginTop: fluid(width, 36, 56),
              }}
            >
              {DOWNLOAD_PLATFORMS.map((platform) => (
                <PlatformCard key={platform.key} platform={platform} />
              ))}
            </View>
            {isWeb && <View style={{ marginTop: 28, gap: 8 }}>
              <Text weight="medium" style={{ color: c.ink, fontSize: 20 }}>A little Gab AI beside every tab.</Text>
              <Text style={{ color: c.inkSoft, fontSize: 15, lineHeight: 25 }}>Bring side chat to Chrome and Brave with the browser extension. Install from the Chrome Web Store, with automatic browser updates.</Text>
              <GhostCta label="Add to Chrome or Brave" href={EXTENSION_STORE_URL} />
            </View>}
          </Reveal>
        </Section>

        {/* ============ SYNC BAND ============ */}
        <View
          style={{
            width: '100%',
            alignSelf: 'stretch',
            alignItems: 'center',
            backgroundColor: band.bg,
            overflow: 'hidden',
            borderTopWidth: 1,
            borderBottomWidth: 1,
            borderColor: band.rule,
          }}
        >
          {isWeb ? (
            <View
              aria-hidden
              pointerEvents="none"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `radial-gradient(ellipse 50% 70% at 12% 40%, ${band.glow}, transparent 68%)`,
              }}
            />
          ) : null}

          <Section label="Cross-device sync" background="transparent">
            <Split ratio={[1, 1.1]} align="flex-start">
              <Reveal>
                <Kicker tone="band">One account</Kicker>
                <DisplayHeading
                  tag="h2"
                  mask={false}
                  type={h2}
                  color={band.ink}
                  style={{ marginTop: 22 }}
                  lines={[['Everything stays'], ['in sync.']]}
                />
                <Lede tone="band" max={400} style={{ marginTop: 22 }}>
                  You are not juggling separate mobile and desktop products.
                  Sign in anywhere and the workspace you left is the workspace you get.
                </Lede>
                <GhostCta label="Import chats from ChatGPT" href="/switch" tone="band" style={{ marginTop: 8 }} />
              </Reveal>

              <Reveal delay={120}>
                <View
                  style={{
                    borderRadius: 18,
                    borderWidth: 1,
                    borderColor: band.rule,
                    backgroundColor: band.surfaceSoft,
                    paddingHorizontal: isXS ? 16 : 22,
                    paddingVertical: 6,
                  }}
                >
                  {DOWNLOAD_SYNC_POINTS.map((point, index) => (
                    <SyncRow
                      key={point.title}
                      point={point}
                      width={width}
                      isLast={index === DOWNLOAD_SYNC_POINTS.length - 1}
                    />
                  ))}
                </View>
              </Reveal>
            </Split>
          </Section>
        </View>

        {/* ============ FAQ ============ */}
        <Section label="Download questions">
          <Split ratio={[1, 1.5]} align="flex-start">
            <Reveal>
              <Kicker>Answers</Kicker>
              <DisplayHeading
                tag="h2"
                mask={false}
                type={h2}
                style={{ marginTop: 22 }}
                lines={[['Before you'], ['install.']]}
              />
              <Lede size="sm" max={340} style={{ marginTop: 20 }}>
                Still deciding? Compare plans, read the docs, or email support —
                we answer quickly.
              </Lede>
              <GhostCta label="View pricing" href="/pricing" style={{ marginTop: 12 }} />
            </Reveal>

            <Reveal delay={100}>
              <View style={{ borderTopWidth: 1, borderTopColor: c.rule }}>
                {FAQS.map((item) => (
                  <View
                    key={item.q}
                    style={{
                      paddingVertical: fluid(width, 18, 24),
                      borderBottomWidth: 1,
                      borderBottomColor: c.rule,
                      gap: 8,
                    }}
                  >
                    <Text
                      tag="h3"
                      weight="bold"
                      style={{ color: c.ink, fontSize: 16.5, letterSpacing: -0.2, lineHeight: 24 }}
                    >
                      {item.q}
                    </Text>
                    <Text tag="p" style={{ color: c.muted, fontSize: 14, lineHeight: 22, maxWidth: 620 }}>
                      {item.a}
                    </Text>
                  </View>
                ))}
              </View>
            </Reveal>
          </Split>
        </Section>

        <LandingClosing />
        <PublicFooter variant="editorial" showHero={false} />
      </ScrollView>
    </View>
  );
}
